'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, BookOpen, ArrowRight, Check } from 'lucide-react';
import Link from 'next/link';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { useBrandLogos } from '@/hooks/useBrandLogos';

type LoginForm = {
  email: string;
  password: string;
  remember: boolean;
};

type SignupForm = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  schoolVenue: string;
  role: 'director' | 'coordinator' | 'admin' | 'other';
  terms: boolean;
};

const DEMO_EMAIL = 'director@westsidehigh.edu';
const DEMO_PASSWORD = 'Ovation2026!';

// Program card dimensions matching PopularPrograms (5.75×8.75 ratio)
const PROG_W = 160;
const PROG_H = Math.round(PROG_W / (5.75 / 8.75)); // ≈ 244
const HEADER_H = Math.round(PROG_H * 0.2); // ≈ 49
const CONTENT_H = PROG_H - HEADER_H; // ≈ 195

const PREVIEW_PROGRAMS = [
  {
    id: 'p1',
    title: 'The Phantom of the Opera',
    school: 'Summit Arts Center',
    city: 'Denver, CO',
    badge: 'Trending',
    badgeColor: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
    accentColor: 'rgba(244,63,94,0.7)',
    headerAccent: '#f43f5e',
    rotate: '-4deg',
    opacity: 1,
  },
  {
    id: 'p2',
    title: 'Hamilton',
    school: 'Westside High School',
    city: 'Austin, TX',
    badge: 'Most Viewed',
    badgeColor: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    accentColor: 'rgba(245,158,11,0.7)',
    headerAccent: '#f59e0b',
    rotate: '3deg',
    opacity: 0.7,
  },
];

export default function AuthClient() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [coverImages, setCoverImages] = useState<Record<string, string | null>>({});
  const router = useRouter();
  const { signIn, signUp } = useAuth();
  const { logoWordmarkUrl } = useBrandLogos();

  useEffect(() => {
    async function fetchCovers() {
      const supabase = (await import('@/lib/supabase/client')).createClient();
      const slugs = ['phantom-of-the-opera', 'hamilton'];
      const { data } = await supabase
        .from('musicals')
        .select('slug, cover_image_url')
        .in('slug', slugs);
      if (data) {
        const map: Record<string, string | null> = {};
        data.forEach((row: { slug: string; cover_image_url: string | null }) => {
          map[row.slug] = row.cover_image_url;
        });
        setCoverImages(map);
      }
    }
    fetchCovers();
  }, []);

  const loginForm = useForm<LoginForm>({ defaultValues: { email: '', password: '', remember: false } });
  const signupForm = useForm<SignupForm>({ defaultValues: { role: 'director' } });

  const handleLogin = loginForm.handleSubmit(async (data) => {
    setLoading(true);
    try {
      const result = await signIn(data.email, data.password);
      toast.success('Welcome back! Redirecting...');

      const supabase = (await import('@/lib/supabase/client')).createClient();
      const userId = result?.user?.id;
      if (userId) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('profile_completed')
          .eq('id', userId)
          .single();

        if (!profile?.profile_completed) {
          router.push('/profile-completion');
          router.refresh();
          return;
        }
      }

      router.push('/user-dashboard');
      router.refresh();
    } catch (error: any) {
      toast.error(error?.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  });

  const handleSignup = signupForm.handleSubmit(async (data) => {
    setLoading(true);
    try {
      if (data.password !== data.confirmPassword) {
        signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
        setLoading(false);
        return;
      }
      await signUp(data.email, data.password, {
        fullName: `${data.firstName} ${data.lastName}`,
        schoolVenue: data.schoolVenue,
        role: data.role,
      });
      toast.success('Account created! Let\'s complete your profile.');
      router.push('/profile-completion');
      router.refresh();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  });

  return (
    <div className="min-h-screen flex relative overflow-hidden" style={{ backgroundColor: '#13162a' }}>
      {/* Ambient gradient blobs — matching landing page */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
        <div
          className="absolute rounded-full blur-3xl animate-blob-1"
          style={{ width: '520px', height: '520px', top: '-80px', left: '-120px', background: 'radial-gradient(circle, rgba(139,92,246,0.22) 0%, transparent 70%)' }}
        />
        <div
          className="absolute rounded-full blur-3xl animate-blob-2"
          style={{ width: '400px', height: '400px', bottom: '60px', right: '-80px', background: 'radial-gradient(circle, rgba(236,72,153,0.18) 0%, transparent 70%)' }}
        />
        <div
          className="absolute rounded-full blur-3xl animate-blob-3"
          style={{ width: '340px', height: '340px', top: '40%', left: '35%', background: 'radial-gradient(circle, rgba(245,158,11,0.13) 0%, transparent 70%)' }}
        />
        <div
          className="absolute rounded-full blur-3xl animate-blob-4"
          style={{ width: '300px', height: '300px', bottom: '20%', left: '10%', background: 'radial-gradient(circle, rgba(14,165,233,0.14) 0%, transparent 70%)' }}
        />
      </div>

      {/* Brand panel */}
      <div
        className="hidden lg:flex w-[45%] flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1e2235 0%, #1a1d27 50%, #1e2235 100%)', borderRight: '1px solid rgba(255,255,255,0.08)', zIndex: 1 }}
      >
        {/* Colorful top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, #8b5cf6, #ec4899, #f59e0b, #0ea5e9)' }} />

        {/* Inner blobs for brand panel */}
        <div className="absolute top-24 left-16 w-56 h-56 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.18) 0%, transparent 70%)' }} />
        <div className="absolute bottom-24 right-8 w-44 h-44 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.14) 0%, transparent 70%)' }} />
        <div className="absolute top-1/2 left-1/2 w-40 h-40 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(236,72,153,0.1) 0%, transparent 70%)' }} />

        <div className="flex items-center gap-3 relative">
          <Link href="/landing-page" className="flex items-center gap-3">
            <AppLogo size={36} />
            {logoWordmarkUrl ? (
              <img src={logoWordmarkUrl} alt="Ovation Programs" className="h-7 object-contain" />
            ) : (
              <span className="font-bold text-xl text-gradient-gold">Ovation Programs</span>
            )}
          </Link>
        </div>

        <div className="relative">
          <h1 className="text-4xl font-extrabold text-zinc-100 leading-tight mb-4">
            Theatre programs that
            <span className="text-gradient-gold"> wow audiences.</span>
          </h1>
          <p className="text-zinc-400 leading-relaxed mb-8">
            Create beautiful digital Playbill-style programs for your school or venue. Free to create and share.
          </p>

          <div className="space-y-3">
            {[
              { text: 'Preloaded cast, songs, and billing from 500+ musicals', color: 'rgba(139,92,246,0.15)', border: 'rgba(139,92,246,0.35)', icon: 'text-violet-400' },
              { text: 'Flipbook viewer — shareable link, no app needed', color: 'rgba(236,72,153,0.12)', border: 'rgba(236,72,153,0.3)', icon: 'text-pink-400' },
              { text: '25% cheaper than traditional printing', color: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)', icon: 'text-amber-400' },
              { text: 'Print-ready PDF export in minutes', color: 'rgba(14,165,233,0.12)', border: 'rgba(14,165,233,0.3)', icon: 'text-sky-400' },
            ].map((feat, idx) => (
              <div key={`brand-feat-${idx}`} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: feat.color, border: `1px solid ${feat.border}` }}>
                  <Check size={11} className={feat.icon} />
                </div>
                <span className="text-sm text-zinc-300">{feat.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Program preview mockup */}
        <div className="relative flex gap-4 items-end">
          {PREVIEW_PROGRAMS.map((prog) => {
            const coverUrl = coverImages[prog.title.toLowerCase().replace(/\s+/g, '-')] || null;
            return (
              <div
                key={prog.id}
                className="relative overflow-hidden shrink-0"
                style={{
                  width: PROG_W,
                  height: PROG_H,
                  border: `1px solid ${prog.accentColor}`,
                  borderRadius: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  transform: `rotate(${prog.rotate})`,
                  opacity: prog.opacity,
                }}
              >
                {/* Gold accent line on left edge */}
                <div
                  className="absolute top-0 left-0 w-0.5 h-full z-10"
                  style={{ background: `linear-gradient(180deg, ${prog.headerAccent}, ${prog.accentColor})` }}
                />

                {/* 20% black header */}
                <div
                  className="flex items-center justify-center gap-1.5 px-4 shrink-0"
                  style={{ height: HEADER_H, backgroundColor: '#09090b' }}
                >
                  {logoWordmarkUrl ? (
                    <img
                      src={logoWordmarkUrl}
                      alt="Ovation"
                      className="object-contain"
                      style={{ height: '15.6px', filter: 'brightness(0) saturate(100%) invert(1) sepia(1) saturate(2) hue-rotate(0deg)', maxWidth: '80%' }}
                    />
                  ) : (
                    <>
                      <BookOpen size={10} style={{ color: prog.headerAccent }} />
                      <span
                        className="font-bold tracking-[0.15em] uppercase"
                        style={{ fontSize: 7, color: prog.headerAccent }}
                      >
                        Program
                      </span>
                    </>
                  )}
                </div>

                {/* 80% content area */}
                {coverImages[prog.id === 'p1' ? 'phantom-of-the-opera' : 'hamilton'] ? (
                  <div className="relative overflow-hidden" style={{ height: CONTENT_H }}>
                    <img
                      src={coverImages[prog.id === 'p1' ? 'phantom-of-the-opera' : 'hamilton']!}
                      alt={`${prog.title} cover`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-1.5 right-1.5">
                      <span
                        className={`text-[8px] font-bold px-1.5 py-0.5 border ${prog.badgeColor}`}
                        style={{ borderRadius: 2 }}
                      >
                        {prog.badge}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5" style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.7) 0%, transparent 100%)' }}>
                      <div className="text-[8px] font-semibold text-white/90 leading-tight truncate">{prog.school}</div>
                    </div>
                  </div>
                ) : (
                  <div
                    className="flex flex-col items-center justify-between p-2.5 bg-white"
                    style={{ height: CONTENT_H }}
                  >
                    {/* Badge */}
                    <div className="w-full flex justify-end">
                      <span
                        className={`text-[8px] font-bold px-1.5 py-0.5 border ${prog.badgeColor}`}
                        style={{ borderRadius: 2 }}
                      >
                        {prog.badge}
                      </span>
                    </div>

                    {/* Title block */}
                    <div className="flex-1 flex flex-col items-center justify-center text-center px-1">
                      <div className="text-[7px] text-zinc-400 tracking-widest uppercase font-sans mb-1">
                        Musical Program
                      </div>
                      <div className="text-[10px] font-bold text-zinc-900 font-sans leading-tight">
                        {prog.title}
                      </div>
                      <div className="mt-2 w-6 h-px" style={{ backgroundColor: prog.headerAccent }} />
                    </div>

                    {/* School / city */}
                    <div className="w-full">
                      <div className="text-[8px] font-semibold text-zinc-700 leading-tight truncate">
                        {prog.school}
                      </div>
                      <div className="text-[7px] text-zinc-400">{prog.city}</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <div className="text-xs text-zinc-600 ml-1 mb-2 self-end">
            {logoWordmarkUrl ? (
              <img src={logoWordmarkUrl} alt="Ovation" className="h-3 object-contain opacity-50" />
            ) : (
              'Built with Ovation'
            )}
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-8 lg:p-12 relative" style={{ zIndex: 1 }}>
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-2 mb-8">
            <Link href="/landing-page" className="flex items-center gap-2">
              <AppLogo size={28} />
              {logoWordmarkUrl ? (
                <img src={logoWordmarkUrl} alt="Ovation Programs" className="h-6 object-contain" />
              ) : (
                <span className="font-bold text-lg text-gradient-gold">Ovation Programs</span>
              )}
            </Link>
          </div>

          {/* Form card with accent bar */}
          <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#1e2235', border: '1px solid rgba(255,255,255,0.09)', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}>
            {/* Colorful accent bar */}
            <div className="h-1" style={{ background: 'linear-gradient(90deg, #8b5cf6, #ec4899, #f59e0b, #0ea5e9)' }} />

            <div className="p-8">
              {/* Mode toggle */}
              <div className="flex rounded-xl p-1 mb-8" style={{ backgroundColor: '#252a40', border: '1px solid rgba(255,255,255,0.08)' }}>
                <button
                  onClick={() => setMode('login')}
                  className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-150 ${
                    mode === 'login' ? 'text-zinc-100' : 'text-zinc-500 hover:text-zinc-300'
                  }`}
                  style={mode === 'login' ? { background: 'linear-gradient(135deg, rgba(139,92,246,0.3), rgba(236,72,153,0.2))', border: '1px solid rgba(139,92,246,0.3)' } : {}}
                >
                  Sign In
                </button>
                <button
                  onClick={() => setMode('signup')}
                  className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-150 ${
                    mode === 'signup' ? 'text-zinc-100' : 'text-zinc-500 hover:text-zinc-300'
                  }`}
                  style={mode === 'signup' ? { background: 'linear-gradient(135deg, rgba(139,92,246,0.3), rgba(236,72,153,0.2))', border: '1px solid rgba(139,92,246,0.3)' } : {}}
                >
                  Create Account
                </button>
              </div>

              {/* LOGIN FORM */}
              {mode === 'login' && (
                <form onSubmit={handleLogin} className="space-y-5 animate-fade-in">
                  <div>
                    <h2 className="text-2xl font-extrabold text-zinc-100 mb-1">Welcome back</h2>
                    <p className="text-sm text-zinc-500">Sign in to your Ovation account</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-1.5">Email address</label>
                    <input
                      type="email"
                      {...loginForm.register('email', { required: 'Email is required' })}
                      placeholder="you@school.edu"
                      className="w-full rounded-xl px-4 py-3 text-sm text-zinc-100 placeholder-zinc-600 outline-none transition-colors"
                      style={{ backgroundColor: '#252a40', border: '1px solid rgba(255,255,255,0.1)' }}
                      onFocus={(e) => (e.target.style.borderColor = 'rgba(139,92,246,0.6)')}
                      onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                    />
                    {loginForm.formState.errors.email && (
                      <p className="text-rose-400 text-xs mt-1">{loginForm.formState.errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-1.5">Password</label>
                    <div className="relative">
                      <input
                        type={showPass ? 'text' : 'password'}
                        {...loginForm.register('password', { required: 'Password is required' })}
                        placeholder="••••••••"
                        className="w-full rounded-xl px-4 py-3 text-sm text-zinc-100 placeholder-zinc-600 outline-none transition-colors pr-12"
                        style={{ backgroundColor: '#252a40', border: '1px solid rgba(255,255,255,0.1)' }}
                        onFocus={(e) => (e.target.style.borderColor = 'rgba(139,92,246,0.6)')}
                        onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPass(!showPass)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                      >
                        {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {loginForm.formState.errors.password && (
                      <p className="text-rose-400 text-xs mt-1">{loginForm.formState.errors.password.message}</p>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" {...loginForm.register('remember')} className="w-4 h-4 rounded accent-violet-500" style={{ backgroundColor: '#252a40', borderColor: 'rgba(255,255,255,0.2)' }} />
                      <span className="text-sm text-zinc-400">Remember me</span>
                    </label>
                    <button type="button" className="text-sm text-violet-400 hover:text-violet-300 font-medium transition-colors">
                      Forgot password?
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 font-bold py-3 rounded-xl transition-all duration-150 active:scale-95 disabled:opacity-60 text-white"
                    style={{ minHeight: '48px', background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 60%, #f59e0b 100%)', boxShadow: '0 4px 20px rgba(139,92,246,0.4)' }}
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>Sign In <ArrowRight size={16} /></>
                    )}
                  </button>
                </form>
              )}

              {/* SIGNUP FORM */}
              {mode === 'signup' && (
                <form onSubmit={handleSignup} className="space-y-4 animate-fade-in">
                  <div>
                    <h2 className="text-2xl font-extrabold text-zinc-100 mb-1">Create your account</h2>
                    <p className="text-sm text-zinc-500">Free forever — pay only for PDF exports</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-zinc-300 mb-1.5">First name</label>
                      <input
                        type="text"
                        {...signupForm.register('firstName', { required: 'Required' })}
                        placeholder="Rachel"
                        className="w-full rounded-xl px-4 py-3 text-sm text-zinc-100 placeholder-zinc-600 outline-none transition-colors"
                        style={{ backgroundColor: '#252a40', border: '1px solid rgba(255,255,255,0.1)' }}
                        onFocus={(e) => (e.target.style.borderColor = 'rgba(139,92,246,0.6)')}
                        onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                      />
                      {signupForm.formState.errors.firstName && (
                        <p className="text-rose-400 text-xs mt-1">{signupForm.formState.errors.firstName.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-zinc-300 mb-1.5">Last name</label>
                      <input
                        type="text"
                        {...signupForm.register('lastName', { required: 'Required' })}
                        placeholder="Torres"
                        className="w-full rounded-xl px-4 py-3 text-sm text-zinc-100 placeholder-zinc-600 outline-none transition-colors"
                        style={{ backgroundColor: '#252a40', border: '1px solid rgba(255,255,255,0.1)' }}
                        onFocus={(e) => (e.target.style.borderColor = 'rgba(139,92,246,0.6)')}
                        onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-1.5">Email address</label>
                    <input
                      type="email"
                      {...signupForm.register('email', { required: 'Email is required' })}
                      placeholder="you@school.edu"
                      className="w-full rounded-xl px-4 py-3 text-sm text-zinc-100 placeholder-zinc-600 outline-none transition-colors"
                      style={{ backgroundColor: '#252a40', border: '1px solid rgba(255,255,255,0.1)' }}
                      onFocus={(e) => (e.target.style.borderColor = 'rgba(139,92,246,0.6)')}
                      onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                    />
                    {signupForm.formState.errors.email && (
                      <p className="text-rose-400 text-xs mt-1">{signupForm.formState.errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-1.5">School or Venue</label>
                    <p className="text-xs text-zinc-600 mb-1.5">Start typing to find your school or add a new one</p>
                    <input
                      type="text"
                      {...signupForm.register('schoolVenue', { required: 'School or venue is required' })}
                      placeholder="Westside High School, Austin TX"
                      className="w-full rounded-xl px-4 py-3 text-sm text-zinc-100 placeholder-zinc-600 outline-none transition-colors"
                      style={{ backgroundColor: '#252a40', border: '1px solid rgba(255,255,255,0.1)' }}
                      onFocus={(e) => (e.target.style.borderColor = 'rgba(139,92,246,0.6)')}
                      onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                    />
                    {signupForm.formState.errors.schoolVenue && (
                      <p className="text-rose-400 text-xs mt-1">{signupForm.formState.errors.schoolVenue.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-1.5">Your role</label>
                    <select
                      {...signupForm.register('role')}
                      className="w-full rounded-xl px-4 py-3 text-sm text-zinc-100 outline-none transition-colors"
                      style={{ backgroundColor: '#252a40', border: '1px solid rgba(255,255,255,0.1)' }}
                      onFocus={(e) => (e.target.style.borderColor = 'rgba(139,92,246,0.6)')}
                      onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                    >
                      <option value="director" style={{ backgroundColor: '#252a40' }}>Theatre Director</option>
                      <option value="coordinator" style={{ backgroundColor: '#252a40' }}>Production Coordinator</option>
                      <option value="admin" style={{ backgroundColor: '#252a40' }}>School Administrator</option>
                      <option value="other" style={{ backgroundColor: '#252a40' }}>Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-1.5">Password</label>
                    <div className="relative">
                      <input
                        type={showPass ? 'text' : 'password'}
                        {...signupForm.register('password', { required: 'Password is required', minLength: { value: 8, message: 'Minimum 8 characters' } })}
                        placeholder="Min. 8 characters"
                        className="w-full rounded-xl px-4 py-3 text-sm text-zinc-100 placeholder-zinc-600 outline-none transition-colors pr-12"
                        style={{ backgroundColor: '#252a40', border: '1px solid rgba(255,255,255,0.1)' }}
                        onFocus={(e) => (e.target.style.borderColor = 'rgba(139,92,246,0.6)')}
                        onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                      />
                      <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                        {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {signupForm.formState.errors.password && (
                      <p className="text-rose-400 text-xs mt-1">{signupForm.formState.errors.password.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-1.5">Confirm password</label>
                    <div className="relative">
                      <input
                        type={showConfirm ? 'text' : 'password'}
                        {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })}
                        placeholder="••••••••"
                        className="w-full rounded-xl px-4 py-3 text-sm text-zinc-100 placeholder-zinc-600 outline-none transition-colors pr-12"
                        style={{ backgroundColor: '#252a40', border: '1px solid rgba(255,255,255,0.1)' }}
                        onFocus={(e) => (e.target.style.borderColor = 'rgba(139,92,246,0.6)')}
                        onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                      />
                      <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                        {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {signupForm.formState.errors.confirmPassword && (
                      <p className="text-rose-400 text-xs mt-1">{signupForm.formState.errors.confirmPassword.message}</p>
                    )}
                  </div>

                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" {...signupForm.register('terms', { required: 'You must accept the terms' })} className="w-4 h-4 mt-0.5 rounded accent-violet-500 shrink-0" />
                    <span className="text-xs text-zinc-400">
                      I agree to the{' '}
                      <a href="#" className="text-violet-400 hover:underline">Terms of Service</a>{' '}
                      and{' '}
                      <a href="#" className="text-violet-400 hover:underline">Privacy Policy</a>
                    </span>
                  </label>
                  {signupForm.formState.errors.terms && (
                    <p className="text-rose-400 text-xs">{signupForm.formState.errors.terms.message}</p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 font-bold py-3 rounded-xl transition-all duration-150 active:scale-95 disabled:opacity-60 text-white"
                    style={{ minHeight: '48px', background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 60%, #f59e0b 100%)', boxShadow: '0 4px 20px rgba(139,92,246,0.4)' }}
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>Create Free Account <ArrowRight size={16} /></>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

          <div className="mt-6 text-center">
            <Link href="/landing-page" className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors">
              ← Back to homepage
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}